import { FC, ReactElement, useCallback, useState } from 'react';

import { useAudioTrigger } from '@pp/core/src/controllers/audioController/hooks';
import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { useChipSlice } from '@pp/core/src/store/slices/chips/selectors';
import { useSettingsSlice } from '@pp/core/src/store/slices/settings/selectors';
import { useIsLandscape } from '@pp/core/src/styles/hooks/media';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { Bets } from '../../config/bets';
import { useGameDispatch } from '../../store/GameStore';
import { betStepperActions } from '../../store/slices/betStepper/slice';
import { useHighflyerGameStateSlice } from '../../store/slices/highflyerGame/selectors';
import { useHighflyerStateSlice } from '../../store/slices/highflyerState/selectors';
import { useValidBetInputValue, useBetsInputToolTip } from '../gameGrid/hooks';

import { QuickBetConnected } from './QuickBetConnected';

import styles from './QuickBet.module.css';

export interface QuickBetWingProps {
  betCode: Bets;
}
export const QuickBetWing: FC<QuickBetWingProps> = ({ betCode }): ReactElement => {
  const chips = useChipSlice.getChips();
  const dispatch = useGameDispatch();
  const { betInputAmount } = useValidBetInputValue(betCode);
  const { balanceInSufficientReached } = useBetsInputToolTip(betCode);

  const isSecondBetBoardEnabled = useHighflyerGameStateSlice.getIsSecondBetBoardToggle();
  const isLandscape = useIsLandscape();
  const locale = useSettingsSlice.currentLanguage();
  const audioTrigger = useAudioTrigger();
  const defaultToolTipText = 'ADD_AMOUNT';
  const betLimits = useBetSlice.betLimits() ?? {};
  const [tooltipText, setTooltipText] = useState(defaultToolTipText);
  const remainingBalance = useHighflyerStateSlice.balance();

  const onQuickBet = useCallback(
    (incrementalValue: number): void => {
      audioTrigger.play({ name: 'menuClick' });
      let finalBetInput = betInputAmount + incrementalValue;

      if (!balanceInSufficientReached) {
        dispatch(
          betStepperActions.setQuickBetClick({
            betId: betCode,
            value: { click: true, count: Math.random() + 1 * 1000 },
          })
        );
      } else {
        setTooltipText('SHOW_NOT_ENOUGH_MONEY');
      }

      if (finalBetInput > remainingBalance) {
        finalBetInput = remainingBalance;
      }
      if (finalBetInput > betLimits[betCode].max) {
        finalBetInput = betLimits[betCode].max;
      }
      dispatch(
        betStepperActions.setBetInputValue({
          betId: betCode,
          value: finalBetInput.toFixed(2),
          isBetFinalized: true,
        })
      );
    },
    [dispatch, betInputAmount, audioTrigger, remainingBalance, betCode, balanceInSufficientReached]
  );

  const mods = useModifiers(styles, 'quick_bet_wing', {
    forceShow: !isSecondBetBoardEnabled && isLandscape,
  });
  const onPointerLeave = (): void => {
    setTooltipText(defaultToolTipText);
  };

  return (
    <div className={`${styles.quick_bet_wing} ${mods}`}>
      {chips.slice(0, 4).map((chipObj) => {
        return (
          <QuickBetConnected
            key={chipObj.value}
            betCode={betCode}
            value={chipObj.value}
            onQuickBet={onQuickBet}
            locale={locale}
            totalTip={tooltipText}
            pointerLeave={onPointerLeave}
          />
        );
      })}
    </div>
  );
};
